import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { invoiceService } from '../../services/api'
import { 
  Invoice, 
  CreateInvoiceRequest, 
  UpdateInvoiceRequest, 
  InvoiceFilters,
  PaginationParams
} from '../../types/api'
import { isApiError } from '../../utils/api'

interface InvoiceState {
  invoices: Invoice[]
  currentInvoice: Invoice | null
  pagination: {
    page: number
    per_page: number
    total: number
    last_page: number
  }
  filters: InvoiceFilters
  loading: boolean
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  searchResults: Invoice[]
  searchLoading: boolean
  recentInvoices: Invoice[]
  draftInvoices: Invoice[]
  overdueInvoices: Invoice[]
  statistics: {
    total_count: number
    total_amount: number
    paid_amount: number
    pending_amount: number
    overdue_amount: number
    average_amount: number
    status_breakdown: Record<string, number>
  } | null
  calculations: {
    subtotal: number
    tax_amount: number
    discount_amount: number
    total: number
  } | null
  validationResult: {
    valid: boolean
    errors: string[]
    warnings: string[]
  } | null
  bulkOperationStatus: {
    operation: 'update' | 'delete' | 'export' | null
    progress: number
    completed: number
    failed: number
    total: number
  }
}

const initialState: InvoiceState = {
  invoices: [],
  currentInvoice: null,
  pagination: {
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  },
  filters: {},
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
  searchResults: [],
  searchLoading: false,
  recentInvoices: [],
  draftInvoices: [],
  overdueInvoices: [],
  statistics: null,
  calculations: null,
  validationResult: null,
  bulkOperationStatus: {
    operation: null,
    progress: 0,
    completed: 0,
    failed: 0,
    total: 0
  }
}

// Async thunks
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async (params: { filters?: InvoiceFilters; pagination?: PaginationParams }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoices(params.filters, params.pagination)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch invoices')
    }
  }
)

export const fetchInvoice = createAsyncThunk(
  'invoices/fetchInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoice(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch invoice')
    }
  }
)

export const createInvoice = createAsyncThunk(
  'invoices/createInvoice',
  async (data: CreateInvoiceRequest, { rejectWithValue }) => {
    try {
      const response = await invoiceService.createInvoice(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to create invoice')
    }
  }
)

export const updateInvoice = createAsyncThunk(
  'invoices/updateInvoice',
  async ({ id, data }: { id: string; data: UpdateInvoiceRequest }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.updateInvoice(id, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update invoice')
    }
  }
)

export const deleteInvoice = createAsyncThunk(
  'invoices/deleteInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      await invoiceService.deleteInvoice(id)
      return id
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to delete invoice')
    }
  }
)

export const updateInvoiceStatus = createAsyncThunk(
  'invoices/updateStatus',
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.updateStatus(id, status)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update invoice status')
    }
  }
)

export const markInvoiceAsPaid = createAsyncThunk(
  'invoices/markAsPaid',
  async ({ id, paymentData }: { id: string; paymentData?: any }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.markAsPaid(id, paymentData)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to mark invoice as paid')
    }
  }
)

export const duplicateInvoice = createAsyncThunk(
  'invoices/duplicate',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await invoiceService.duplicateInvoice(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to duplicate invoice')
    }
  }
)

export const searchInvoices = createAsyncThunk(
  'invoices/search',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await invoiceService.searchInvoices(query)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to search invoices')
    }
  }
)

export const calculateInvoice = createAsyncThunk(
  'invoices/calculate',
  async (data: CreateInvoiceRequest, { rejectWithValue }) => {
    try {
      const response = await invoiceService.calculateInvoice(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to calculate invoice')
    }
  }
)

export const validateInvoice = createAsyncThunk(
  'invoices/validate',
  async (data: CreateInvoiceRequest, { rejectWithValue }) => {
    try {
      const response = await invoiceService.validateInvoice(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to validate invoice')
    }
  }
)

export const fetchInvoiceStatistics = createAsyncThunk(
  'invoices/fetchStatistics',
  async (filters: any = {}, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoiceStatistics(filters)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch statistics')
    }
  }
)

export const fetchRecentInvoices = createAsyncThunk(
  'invoices/fetchRecent',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getRecentInvoices(limit)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch recent invoices')
    }
  }
)

export const fetchDraftInvoices = createAsyncThunk(
  'invoices/fetchDrafts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getDraftInvoices()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch draft invoices')
    }
  }
)

export const fetchOverdueInvoices = createAsyncThunk(
  'invoices/fetchOverdue',
  async (_, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getOverdueInvoices()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch overdue invoices')
    }
  }
)

export const bulkUpdateInvoices = createAsyncThunk(
  'invoices/bulkUpdate',
  async ({ ids, data }: { ids: string[]; data: Partial<UpdateInvoiceRequest> }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.bulkUpdate(ids, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk update invoices')
    }
  }
)

export const bulkDeleteInvoices = createAsyncThunk(
  'invoices/bulkDelete',
  async (ids: string[], { rejectWithValue }) => {
    try {
      const response = await invoiceService.bulkDelete(ids)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk delete invoices')
    }
  }
)

// Create slice
const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action: PayloadAction<InvoiceFilters>) => {
      state.filters = action.payload
    },
    updateFilters: (state, action: PayloadAction<Partial<InvoiceFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setCurrentInvoice: (state, action: PayloadAction<Invoice | null>) => {
      state.currentInvoice = action.payload
    },
    clearSearchResults: (state) => {
      state.searchResults = []
    },
    clearCalculations: (state) => {
      state.calculations = null
    },
    clearValidationResult: (state) => {
      state.validationResult = null
    },
    resetBulkOperationStatus: (state) => {
      state.bulkOperationStatus = {
        operation: null,
        progress: 0,
        completed: 0,
        failed: 0,
        total: 0
      }
    },
    updateBulkOperationProgress: (state, action: PayloadAction<{
      progress: number
      completed: number
      failed: number
    }>) => {
      state.bulkOperationStatus = {
        ...state.bulkOperationStatus,
        ...action.payload
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch Invoices
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false
        state.invoices = action.payload.data
        state.pagination = {
          page: action.payload.page,
          per_page: action.payload.per_page,
          total: action.payload.total,
          last_page: action.payload.last_page
        }
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Single Invoice
    builder
      .addCase(fetchInvoice.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.loading = false
        state.currentInvoice = action.payload
      })
      .addCase(fetchInvoice.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Create Invoice
    builder
      .addCase(createInvoice.pending, (state) => {
        state.creating = true
        state.error = null
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.creating = false
        state.invoices.unshift(action.payload)
        state.currentInvoice = action.payload
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.creating = false
        state.error = action.payload as string
      })

    // Update Invoice
    builder
      .addCase(updateInvoice.pending, (state) => {
        state.updating = true
        state.error = null
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.updating = false
        const index = state.invoices.findIndex(invoice => invoice.id === action.payload.id)
        if (index !== -1) {
          state.invoices[index] = action.payload
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.updating = false
        state.error = action.payload as string
      })

    // Delete Invoice
    builder
      .addCase(deleteInvoice.pending, (state) => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.deleting = false
        state.invoices = state.invoices.filter(invoice => invoice.id !== action.payload)
        if (state.currentInvoice?.id === action.payload) {
          state.currentInvoice = null
        }
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.deleting = false
        state.error = action.payload as string
      })

    // Update Status
    builder
      .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(invoice => invoice.id === action.payload.id)
        if (index !== -1) {
          state.invoices[index] = action.payload
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload
        }
      })
      .addCase(updateInvoiceStatus.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Mark as Paid
    builder
      .addCase(markInvoiceAsPaid.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(invoice => invoice.id === action.payload.id)
        if (index !== -1) {
          state.invoices[index] = action.payload
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload
        }
      })
      .addCase(markInvoiceAsPaid.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Duplicate Invoice
    builder
      .addCase(duplicateInvoice.fulfilled, (state, action) => {
        state.invoices.unshift(action.payload)
        state.currentInvoice = action.payload
      })
      .addCase(duplicateInvoice.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Search Invoices
    builder
      .addCase(searchInvoices.pending, (state) => {
        state.searchLoading = true
      })
      .addCase(searchInvoices.fulfilled, (state, action) => {
        state.searchLoading = false
        state.searchResults = action.payload
      })
      .addCase(searchInvoices.rejected, (state, action) => {
        state.searchLoading = false
        state.error = action.payload as string
      })

    // Calculate Invoice
    builder
      .addCase(calculateInvoice.fulfilled, (state, action) => {
        state.calculations = action.payload
      })
      .addCase(calculateInvoice.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Validate Invoice
    builder
      .addCase(validateInvoice.fulfilled, (state, action) => {
        state.validationResult = action.payload
      })
      .addCase(validateInvoice.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Statistics
    builder
      .addCase(fetchInvoiceStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload
      })
      .addCase(fetchInvoiceStatistics.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Recent Invoices
    builder
      .addCase(fetchRecentInvoices.fulfilled, (state, action) => {
        state.recentInvoices = action.payload
      })
      .addCase(fetchRecentInvoices.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Draft Invoices
    builder
      .addCase(fetchDraftInvoices.fulfilled, (state, action) => {
        state.draftInvoices = action.payload
      })
      .addCase(fetchDraftInvoices.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Overdue Invoices
    builder
      .addCase(fetchOverdueInvoices.fulfilled, (state, action) => {
        state.overdueInvoices = action.payload
      })
      .addCase(fetchOverdueInvoices.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Bulk Operations
    builder
      .addCase(bulkUpdateInvoices.pending, (state) => {
        state.bulkOperationStatus.operation = 'update'
      })
      .addCase(bulkUpdateInvoices.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.updated,
          failed: action.payload.failed.length,
          total: action.payload.updated + action.payload.failed.length
        }
      })
      .addCase(bulkUpdateInvoices.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })

    builder
      .addCase(bulkDeleteInvoices.pending, (state) => {
        state.bulkOperationStatus.operation = 'delete'
      })
      .addCase(bulkDeleteInvoices.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.deleted,
          failed: action.payload.failed.length,
          total: action.payload.deleted + action.payload.failed.length
        }
      })
      .addCase(bulkDeleteInvoices.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })
  },
})

// Export actions
export const {
  clearError,
  setFilters,
  updateFilters,
  clearFilters,
  setPagination,
  setCurrentInvoice,
  clearSearchResults,
  clearCalculations,
  clearValidationResult,
  resetBulkOperationStatus,
  updateBulkOperationProgress
} = invoiceSlice.actions

// Selectors
export const selectInvoices = (state: { invoices: InvoiceState }) => state.invoices.invoices
export const selectCurrentInvoice = (state: { invoices: InvoiceState }) => state.invoices.currentInvoice
export const selectInvoicePagination = (state: { invoices: InvoiceState }) => state.invoices.pagination
export const selectInvoiceFilters = (state: { invoices: InvoiceState }) => state.invoices.filters
export const selectInvoiceLoading = (state: { invoices: InvoiceState }) => state.invoices.loading
export const selectInvoiceError = (state: { invoices: InvoiceState }) => state.invoices.error
export const selectSearchResults = (state: { invoices: InvoiceState }) => state.invoices.searchResults
export const selectRecentInvoices = (state: { invoices: InvoiceState }) => state.invoices.recentInvoices
export const selectDraftInvoices = (state: { invoices: InvoiceState }) => state.invoices.draftInvoices
export const selectOverdueInvoices = (state: { invoices: InvoiceState }) => state.invoices.overdueInvoices
export const selectInvoiceStatistics = (state: { invoices: InvoiceState }) => state.invoices.statistics
export const selectCalculations = (state: { invoices: InvoiceState }) => state.invoices.calculations
export const selectValidationResult = (state: { invoices: InvoiceState }) => state.invoices.validationResult
export const selectBulkOperationStatus = (state: { invoices: InvoiceState }) => state.invoices.bulkOperationStatus

export default invoiceSlice.reducer 